<script setup lang="ts">
import {
    ref,
    reactive,
    onMounted,
    nextTick,
    computed,
    watch,
    onUnmounted,
} from 'vue'
import type { PropType } from 'vue'
import MobileUgcDetailItemInfo from '~/components/mobile/MobileUgcDetailItemInfo.vue'
import { ElMessage } from 'element-plus'
import { useI18n } from '~/composables/useI18n'
import {
    getInspirationListApi,
    changeUserLikeStateApi,
    getInspirationDetailApi,
} from '~/api/inspiration'
import {
    canShowGenerateSimilar,
    getInspirationType,
    formatPath,
    getInspirationOutputType,
} from '~/utils/inspiration'
import { getResourceThumbnail } from '~/utils/common'
import { emitter, showLoginModal } from '~/utils/emitter'
import {
    buildWorkbenchPrefillFromInspirationItem,
    isInChatPageRuntime,
    writeWorkbenchInspirationPrefillToStorage,
} from '~/utils/workbenchInspirationPrefill'
import { useMusicPlayerStore } from '~/stores/musicPlayer'
import { useUserStore } from '~/stores/user'
import { analyticsEvent } from '~/utils/log-event-func'
import { useAppStore } from '~/stores/app'
import { getResponsiveWaterfallColumns } from '~/utils/responsive-waterfall'

const { t } = useI18n()
const route = useRoute()
const userStore = useUserStore()
const appStore = useAppStore()
const isLoggedIn = computed(() => userStore.isLoggedIn)
const musicPlayer = useMusicPlayerStore()
const getInspirationId = (item: any) => item?.ins_id || item?.id

// 判断是否在 ai-image 或 ai-video 路由（不显示工具盒子）
const isAiToolRoute = computed(() =>
    route.path.includes('/ai-image') || route.path.includes('/ai-video')
)

// ==================== 音乐类型支持 ====================
// 判断是否是音乐灵感类型
const isMusicType = computed(
    () => props.inspirationType === 'MUSIC_CREATION_GENERATION'
)

type InspirationSkeletonAspectRatioKey = '1:1' | '3:4' | '4:5' | '9:16'

const inspirationSkeletonSequence: InspirationSkeletonAspectRatioKey[] = [
    '3:4',
    '1:1',
    '9:16',
    '4:5',
    '3:4',
    '4:5',
    '1:1',
    '9:16',
    '3:4',
    '4:5',
    '9:16',
    '1:1',
    '3:4',
    '4:5',
    '1:1',
    '9:16',
    '3:4',
    '4:5',
    '9:16',
    '1:1',
    '4:5',
    '3:4',
    '1:1',
    '9:16',
    '3:4',
]
const inspirationSkeletonAspectRatios: Record<InspirationSkeletonAspectRatioKey, number> = {
    '1:1': 1,
    '3:4': 3 / 4,
    '4:5': 4 / 5,
    '9:16': 9 / 16,
}
const inspirationSkeletonCards = inspirationSkeletonSequence.map(
    (aspectRatio, index) => ({
        id: index + 1,
        aspectRatio,
    })
)
const musicSkeletonCards = Array.from({ length: 8 }, (_, index) => index + 1)

const shouldShowGenerateSimilar = (item: any) =>
    canShowGenerateSimilar(item?.type as string | undefined)

// 获取音乐类型标签
const getMusicTypeLabel = (item: any) => {
    const type = item.param?.type
    if (type === 'VOCAL_SONG') {
        return t('menus.aiMusicPage.vocal')
    } else if (type === 'ONLY_MUSIC') {
        return t('menus.aiMusicPage.instMusic')
    }
    return t('menus.aiMusicPage.vocal')
}

// 格式化点赞数
const formatLikeCount = (count: number | undefined) => {
    if (!count) return '0'
    if (count >= 10000) {
        return (count / 10000).toFixed(1) + 'w'
    }
    if (count >= 1000) {
        return (count / 1000).toFixed(1) + 'k'
    }
    return count.toString()
}

// 播放/暂停音频
const togglePlay = (item: any) => {
    const itemId = getInspirationId(item)
    const audioUrl = item.output_audio || item.output
    if (!audioUrl) {
        ElMessage.warning(t('inspiration.noAudio') || '暂无音频')
        return
    }

    // 使用全局播放器播放
    musicPlayer.play(itemId, audioUrl)
}

// 上报特效详情页事件
const sendEffectsDetailsEvent = (slug: string, from: string = 'create') => {
    if (!import.meta.client) return
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
    const local_channel = localStorage.getItem('channel') || 'official'
    analyticsEvent('click_linggan_card', {
        effect_id: slug,
        from,
        uid: userInfo?.uid,
        channel: local_channel,
    })
}

// 音乐做同款
const handleMusicMakeSimilar = (item: any) => {
    // Chat page: do NOT navigate; only prefill PcWorkbench.
    if (isInChatPageRuntime()) {
        const prefill = buildWorkbenchPrefillFromInspirationItem(item)
        if (prefill) {
            emitter.emit('workbench-apply-prefill', prefill)
        }
        return
    }

    // 非 chat：同时回填 PcWorkbench（工具页底部工作台）+ 维持原有音乐页回填逻辑
    const prefill = buildWorkbenchPrefillFromInspirationItem(item)
    if (prefill) {
        writeWorkbenchInspirationPrefillToStorage(prefill)
        // Apply immediately so tool pages with persistent PcWorkbench update without remount.
        // Preserve current workbench compact/expand UI state.
        emitter.emit('workbench-apply-prefill', {
            prefill,
            preserveWorkbenchState: true,
        })
    }

    // 通过事件总线发送数据，填充 text-to-music 页面表单（原有逻辑）
    emitter.emit('fill-music-form', item)

    // 如果不在 text-to-music 页面，则跳转
    if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname
        const inspirationId = getInspirationId(item)
        if (inspirationId) {
            localStorage.setItem('inspirationInsId', String(inspirationId))
        }
        sendEffectsDetailsEvent(item?.id)

        const timestamp = Date.now()
        localStorage.setItem('inspirationData', JSON.stringify(item))

        if (!currentPath.includes('/text-to-music')) {
            navigateTo(`/text-to-music?customType=inspiration&t=${timestamp}`)
        }
    }
}

// 打开音乐详情弹窗（转换数据格式以适配 UgcDetailItemInfo 组件）
const openMusicMask = (item: any) => {
    console.log(item, '音乐数据')
    showMask.value = true
    // 转换数据格式：API 返回的 param.lyrics 需要映射到 music.generated_lyrics
    currentDetailItem.value = {
        ...item,
        music: {
            music_title: item.music?.music_title || '', // 使用 prompt 作为标题
            generated_lyrics: item.music?.generated_lyrics || '', // 歌词从 param.lyrics 获取
            music_style: item.param?.styles?.join(', ') || '',
        },
    }
}
const tempData = ref<any[]>([]) // 初始为空，等待异步加载 - 瀑布流数据容器
const resData = ref<any[]>([]) // 响应数据
const total_page = ref(0) // 总页数
const total_inspiration_data = ref(-1) //总数据量

// 仅用于详情弹窗切换：移动端用 MobileUgcDetailItemInfo，PC 继续沿用 tool-ugc-detail-item-info
const isMobile = ref(false)
const updateIsMobile = () => {
    if (typeof window === 'undefined') return
    isMobile.value = window.innerWidth < 768
}

// ===== 图片渐进加载（thumbnail -> origin，保证不闪）=====
const inspirationImageLoadedState = reactive<Record<string, boolean>>({})

const getInspirationImageKey = (item: any) => {
    return `${String(getInspirationId(item) || '')}|${String(item?.output || '')}`
}

const isInspirationImageLoaded = (item: any) => {
    const key = getInspirationImageKey(item)
    return key ? !!inspirationImageLoadedState[key] : false
}

const markInspirationImageLoaded = (item: any) => {
    const key = getInspirationImageKey(item)
    if (!key) return
    inspirationImageLoadedState[key] = true
}

const markInspirationImageError = (item: any) => {
    const key = getInspirationImageKey(item)
    if (!key) return
    // 失败保持 thumbnail
    inspirationImageLoadedState[key] = false
}
// 显示类型
type WaterfallLayoutOverrides = Partial<{
    baseColumnWidth: number
    maxColumnWidth: number
    minColumnWidth: number
    gap: number
    /**
     * 固定列数（可选）
     * - 不传：按列宽自动计算列数（保持现有行为）
     * - 传入：固定展示 N 列（>=1 的整数）
     */
    columns: number
}>

const props = defineProps({
    // 判断是遮罩层还是卡片显示，动态调整更多按钮
    showType: {
        type: String,
        default: '',
    },
    category: {
        type: String,
        default: '',
    },
    inspirationType: {
        type: String,
        default: '',
    },
    types: {
        type: Array<string>,
        default: [],
    },
    /**
     * 瀑布流布局配置（可选）
     * - 不传：使用组件内默认值（保持原 UI/功能）
     * - 仅支持覆盖：列宽 base/min/max + gap
     */
    waterfallConfig: {
        type: Object as PropType<WaterfallLayoutOverrides>,
        default: () => ({}),
    },
    uiVariant: {
        type: String,
        default: '',
    },
    workbenchBottomInset: {
        type: Number,
        default: 0,
    },
})

const emit = defineEmits<{
    (e: 'scroll-state', payload: { scrollTop: number; distanceFromBottom: number }): void
}>()

// 瀑布流配置
const defaultWaterfallConfig = {
    baseColumnWidth: 240, // 基准列宽
    maxColumnWidth: 300, // 最大列宽
    minColumnWidth: 180, // 最小列宽
    gap: 16,
    minColumns: 1,
    defaultAspectRatio: 3 / 4,
}

const isToolPagePcUiVariant = computed(
    () => props.uiVariant === 'tool-page-pc' && !isMobile.value
)

const config = computed(() => {
    const o = props.waterfallConfig || {}
    const pickNumber = (v: unknown, fallback: number) =>
        typeof v === 'number' && !Number.isNaN(v) ? v : fallback
    const pickPositiveIntOrUndefined = (v: unknown) => {
        if (typeof v !== 'number' || !Number.isFinite(v)) return undefined
        const n = Math.floor(v)
        return n >= 1 ? n : undefined
    }

    const defaultGap = !isMobile.value ? 2 : defaultWaterfallConfig.gap
    const baseGap = pickNumber(o.gap, defaultGap)

    return {
        ...defaultWaterfallConfig,
        baseColumnWidth: pickNumber(
            o.baseColumnWidth,
            defaultWaterfallConfig.baseColumnWidth
        ),
        maxColumnWidth: pickNumber(
            o.maxColumnWidth,
            defaultWaterfallConfig.maxColumnWidth
        ),
        minColumnWidth: pickNumber(
            o.minColumnWidth,
            defaultWaterfallConfig.minColumnWidth
        ),
        gap: baseGap,
        fixedColumns: pickPositiveIntOrUndefined(o.columns),
    }
})

// 容器宽度为 0（不可见）时会导致误判为单列，这里用一个很小的阈值判断“可布局”
const MIN_VISIBLE_WIDTH = 50

// 容器引用
const containerRef = ref<HTMLElement | null>(null)

// 瀑布流状态
const waterfall = ref({
    columns: 2,
    columnWidth: config.value.baseColumnWidth, // 动态列宽
    columnHeights: [] as number[],
    items: [] as any[],
})
const skeletonWaterfall = ref({
    columns: 2,
    columnWidth: config.value.baseColumnWidth,
    columnHeights: [] as number[],
    items: [] as Array<{
        id: number
        aspectRatio: InspirationSkeletonAspectRatioKey
        style: Record<string, string>
    }>,
})

// 计算列数和列宽
const calculateColumnsAndWidth = () => {
    const cfg = config.value
    if (!containerRef.value)
        return {
            columns: cfg.minColumns,
            columnWidth: cfg.baseColumnWidth,
        }

    const containerWidth = containerRef.value.offsetWidth

    // 固定列数：父组件指定 columns 时优先生效
    if (cfg.fixedColumns) {
        const columns = Math.max(cfg.fixedColumns, cfg.minColumns)
        // 公式：列宽 = (容器宽度 - (列数-1) × 间距) / 列数
        const fitWidth =
            (containerWidth - (columns - 1) * cfg.gap) / columns

        // 为避免溢出：最小列宽不会把列宽“抬高到超出 fitWidth”
        let columnWidth = fitWidth
        columnWidth = Math.min(columnWidth, cfg.maxColumnWidth)
        columnWidth = Math.max(columnWidth, Math.min(cfg.minColumnWidth, fitWidth))

        return { columns, columnWidth }
    }

    // 工具页 PC 版与首页灵感区对齐：仅复用固定断点列数规则，
    // 列间距与列宽仍沿用当前工具页布局方式，避免影响既有 UI/交互。
    if (isToolPagePcUiVariant.value) {
        const columns = Math.max(
            getResponsiveWaterfallColumns(containerWidth),
            cfg.minColumns
        )
        const columnWidth =
            (containerWidth - (columns - 1) * cfg.gap) / columns

        return { columns, columnWidth }
    }

    /**
     * 小红书式体验：移动端尽量 2 列（通过降低基准/最小列宽实现）
     * - 只影响本组件，不影响其它瀑布流/组件
     * - 以“容器实际宽度”为准（比 window.innerWidth 更可靠）
     */
    const getResponsiveColConfig = (w: number) => {
        if (w <= 360) return { base: 150, min: 140 } // 超小屏
        if (w <= 420) return { base: 180, min: 160 } // iPhone SE / 小屏手机
        if (w <= 768) return { base: 220, min: 170 } // 平板/小屏桌面
        return {
            base: cfg.baseColumnWidth,
            min: cfg.minColumnWidth,
        } // 桌面保持原逻辑
    }

    const { base, min } = getResponsiveColConfig(containerWidth)

    // 先按基准列宽计算列数
    let columns = Math.floor(
        (containerWidth + cfg.gap) / (base + cfg.gap)
    )
    columns = Math.max(columns, cfg.minColumns)

    // 根据列数反推最佳列宽（让内容填满容器）
    // 公式：列宽 = (容器宽度 - (列数-1) × 间距) / 列数
    let columnWidth = (containerWidth - (columns - 1) * cfg.gap) / columns

    // 限制列宽范围
    columnWidth = Math.max(min, Math.min(cfg.maxColumnWidth, columnWidth))

    return { columns, columnWidth }
}

// 初始化列高度数组
const initColumnHeights = (
    targetState: { columnHeights: number[] },
    columnCount: number
) => {
    targetState.columnHeights = new Array(columnCount).fill(0)
}

// 等待容器宽度可用后再进行布局，避免不可见时宽度为 0 造成单列闪烁
const waitForContainerReady = async () => {
    if (!containerRef.value) return
    await new Promise<void>(resolve => {
        const check = () => {
            const width = containerRef.value?.offsetWidth || 0
            if (width > MIN_VISIBLE_WIDTH) {
                resolve()
            } else {
                requestAnimationFrame(check)
            }
        }
        check()
    })
}

const waitForWorkbenchInsetStable = async () => {
    if (!isToolPagePcUiVariant.value || typeof window === 'undefined') return

    await new Promise<void>(resolve => {
        let lastValue = props.workbenchBottomInset
        let stableFrames = 0
        let frameCount = 0
        const maxFrames = 12

        const check = () => {
            frameCount++
            const currentValue = props.workbenchBottomInset

            if (currentValue === lastValue) {
                stableFrames++
            } else {
                lastValue = currentValue
                stableFrames = 0
            }

            const insetSettled = currentValue > 0 && stableFrames >= 2
            const waitedLongEnough = frameCount >= maxFrames

            if (insetSettled || waitedLongEnough) {
                resolve()
                return
            }

            requestAnimationFrame(check)
        }

        requestAnimationFrame(check)
    })
}

// 获取最短列的索引
const getShortestColumnIndex = (columnHeights: number[]) => {
    const minHeight = Math.min(...columnHeights)
    return columnHeights.indexOf(minHeight)
}

// 计算元素的高度（基于动态列宽和宽高比）
const calculateItemHeight = (
    item: any,
    columnWidth: number = waterfall.value.columnWidth
) => {
    const cfg = config.value
    let aspectRatio = cfg.defaultAspectRatio
    let source = 'default'
    const measuredAspectRatio = getMeasuredAspectRatio(item)

    if (measuredAspectRatio) {
        aspectRatio = measuredAspectRatio
        source = 'measured'
    }

    if (source === 'default' && item.param?.aspect_ratio) {
        // 验证 aspect_ratio 格式是否正确（应包含 ':' 且能解析为数字）
        const ratioStr = item.param.aspect_ratio
        if (ratioStr && ratioStr.includes(':')) {
            const parts = ratioStr.split(':')
            const width = Number(parts[0])
            const height = Number(parts[1])

            // 确保解析成功且都是有效数字
            if (!isNaN(width) && !isNaN(height) && width > 0 && height > 0) {
                aspectRatio = width / height
            } else {
                console.warn(
                    `⚠️ 无效的宽高比格式: "${ratioStr}", 使用默认值 ${cfg.defaultAspectRatio}`
                )
            }
        } else {
            console.warn(
                `⚠️ aspect_ratio 不是宽高比格式: "${ratioStr}", 使用默认值 ${cfg.defaultAspectRatio}`
            )
        }
    }

    return Math.ceil(columnWidth / aspectRatio)
}

// 布局单个元素
const layoutItem = (item: any, index: number) => {
    const cfg = config.value
    const columnIndex = getShortestColumnIndex(waterfall.value.columnHeights)
    // 计算元素高度
    const itemHeight = calculateItemHeight(item)
    // 计算元素位置（使用动态列宽）
    const left = columnIndex * (waterfall.value.columnWidth + cfg.gap)
    const top = waterfall.value.columnHeights[columnIndex]

    // 创建瀑布流元素
    const waterfallItem = {
        ...item,
        style: {
            position: 'absolute',
            left: `${left}px`,
            top: `${top}px`,
            width: `${waterfall.value.columnWidth}px`, // 使用动态列宽
            height: `${itemHeight}px`,
            // 图片懒加载后是否需要重新计算高度
            reCaculateSize: false,
            originIndex: index,
        },
    }
    // 更新该列的高度（验证 itemHeight 是有效数字）
    if (!isNaN(itemHeight) && itemHeight > 0) {
        waterfall.value.columnHeights[columnIndex]! += itemHeight + cfg.gap
    } else {
        console.error('❌ 计算的元素高度无效:', { item, itemHeight })
    }
    return waterfallItem
}

const calculateSkeletonItemHeight = (
    aspectRatio: InspirationSkeletonAspectRatioKey,
    columnWidth: number = skeletonWaterfall.value.columnWidth
) => {
    return Math.ceil(columnWidth / inspirationSkeletonAspectRatios[aspectRatio])
}

const layoutSkeletonItem = (
    item: { id: number; aspectRatio: InspirationSkeletonAspectRatioKey }
) => {
    const cfg = config.value
    const columnIndex = getShortestColumnIndex(skeletonWaterfall.value.columnHeights)
    const itemHeight = calculateSkeletonItemHeight(item.aspectRatio)
    const left = columnIndex * (skeletonWaterfall.value.columnWidth + cfg.gap)
    const top = skeletonWaterfall.value.columnHeights[columnIndex]

    const skeletonItem = {
        ...item,
        style: {
            position: 'absolute',
            left: `${left}px`,
            top: `${top}px`,
            width: `${skeletonWaterfall.value.columnWidth}px`,
            height: `${itemHeight}px`,
        },
    }

    skeletonWaterfall.value.columnHeights[columnIndex]! += itemHeight + cfg.gap

    return skeletonItem
}

const layoutSkeleton = () => {
    if (!containerRef.value) return

    if ((containerRef.value.offsetWidth || 0) <= MIN_VISIBLE_WIDTH) {
        return
    }

    const { columns, columnWidth } = calculateColumnsAndWidth()

    if (
        columns !== skeletonWaterfall.value.columns ||
        columnWidth !== skeletonWaterfall.value.columnWidth
    ) {
        skeletonWaterfall.value.columns = columns
        skeletonWaterfall.value.columnWidth = columnWidth
    }

    initColumnHeights(skeletonWaterfall.value, columns)
    skeletonWaterfall.value.items = inspirationSkeletonCards.map(item =>
        layoutSkeletonItem(item)
    )
}

// 执行瀑布流布局
const syncWaterfallMetrics = () => {
    const { columns, columnWidth } = calculateColumnsAndWidth()
    const hasGeometryChange =
        columns !== waterfall.value.columns ||
        columnWidth !== waterfall.value.columnWidth

    if (hasGeometryChange) {
        waterfall.value.columns = columns
        waterfall.value.columnWidth = columnWidth
    }

    return { columns, hasGeometryChange }
}

const appendWaterfallItems = (items: any[], startIndex: number) => {
    items.forEach((item, index) => {
        const waterfallItem = layoutItem(item, startIndex + index)
        waterfall.value.items.push(waterfallItem)
    })
}

const layout = () => {
    if (!containerRef.value) {
        console.warn('⚠️ 布局中止：containerRef 未挂载')
        return
    }

    // 容器不可见时宽度为 0，直接跳过，等待可见后再布局
    if ((containerRef.value.offsetWidth || 0) <= MIN_VISIBLE_WIDTH) {
        return
    }

    const { columns } = syncWaterfallMetrics()

    // 初始化，清空之前的布局
    initColumnHeights(waterfall.value, columns)

    // 清空旧的瀑布流布局项，避免重复
    waterfall.value.items = []
    appendWaterfallItems(tempData.value, 0)
}

// 视频或图片加载完成时重新计算高度
const MEDIA_RELAYOUT_HEIGHT_THRESHOLD = 24
const MEDIA_RELAYOUT_DEBOUNCE = 120
let mediaRelayoutTimer: ReturnType<typeof setTimeout> | null = null

const scheduleMediaRelayout = () => {
    if (mediaRelayoutTimer) {
        clearTimeout(mediaRelayoutTimer)
    }

    mediaRelayoutTimer = setTimeout(() => {
        mediaRelayoutTimer = null
        layout()
    }, MEDIA_RELAYOUT_DEBOUNCE)
}

const appendLayout = (newItems: any[], startIndex: number) => {
    if (!newItems.length) return

    if (!containerRef.value) {
        layout()
        return
    }

    if ((containerRef.value.offsetWidth || 0) <= MIN_VISIBLE_WIDTH) {
        layout()
        return
    }

    const { columns, hasGeometryChange } = syncWaterfallMetrics()
    const canAppendToExistingLayout =
        !hasGeometryChange &&
        waterfall.value.items.length === startIndex &&
        waterfall.value.columnHeights.length === columns

    if (!canAppendToExistingLayout) {
        layout()
        return
    }

    appendWaterfallItems(newItems, startIndex)
}

const onMediaLoad = (event: Event, item: any) => {
    const target = event.target as HTMLImageElement | HTMLVideoElement

    if (!target) return

    const actualWidth =
        (target as HTMLVideoElement)?.videoWidth ||
        (target as HTMLImageElement).naturalWidth ||
        (target as HTMLImageElement).offsetWidth
    const actualHeight =
        (target as HTMLVideoElement)?.videoHeight ||
        (target as HTMLImageElement).naturalHeight ||
        (target as HTMLImageElement).offsetHeight

    if (actualWidth && actualHeight) {
        const newAspectRatio = actualWidth / actualHeight
        const previousAspectRatio = getMeasuredAspectRatio(item)
        setMeasuredAspectRatio(item, newAspectRatio)
        const oldHeight = parseFloat(item.style.height)
        const newHeight = Math.ceil(
            waterfall.value.columnWidth / newAspectRatio
        ) // 使用动态列宽

        const heightChangedEnough =
            !oldHeight ||
            Math.abs(newHeight - oldHeight) > MEDIA_RELAYOUT_HEIGHT_THRESHOLD
        const aspectRatioChangedEnough =
            !previousAspectRatio ||
            Math.abs(previousAspectRatio - newAspectRatio) > 0.01

        if (heightChangedEnough && aspectRatioChangedEnough) {
            // 合并同一批媒体加载导致的重排，避免瀑布流连续跳动
            scheduleMediaRelayout()
        }
    }
}

// 图片加载事件（thumbnail/origin）
const handleInspirationThumbLoad = (event: Event, item: any) => {
    onMediaLoad(event, item)
}

const handleInspirationOriginLoad = (event: Event, item: any) => {
    markInspirationImageLoaded(item)
    onMediaLoad(event, item)
}

const handleInspirationOriginError = (_event: Event, item: any) => {
    markInspirationImageError(item)
}

// 记录缩略图是否加载失败（避免显示 broken image 图标）
const inspirationThumbErrorState = reactive<Record<string, boolean>>({})

const markInspirationThumbError = (item: any) => {
    const key = getInspirationImageKey(item)
    if (!key) return
    inspirationThumbErrorState[key] = true
}

const isInspirationThumbError = (item: any) => {
    const key = getInspirationImageKey(item)
    return key ? !!inspirationThumbErrorState[key] : false
}

// 节流 + 防抖优化的布局函数
let layoutTimer: ReturnType<typeof setTimeout> | null = null
let lastLayoutTime = 0
const THROTTLE_DELAY = 150 // 节流：最多 150ms 执行一次，保证实时反馈
const DEBOUNCE_DELAY = 300 // 防抖：停止调整后 300ms 执行最后一次，确保最终状态正确

const optimizedLayout = () => {
    const now = Date.now()

    // 清除之前的防抖定时器
    if (layoutTimer) {
        clearTimeout(layoutTimer)
    }

    // 如果距离上次执行超过节流时间，立即执行（提供实时反馈）
    if (now - lastLayoutTime >= THROTTLE_DELAY) {
        lastLayoutTime = now
        if (initLoading.value && !isMusicType.value) {
            layoutSkeleton()
        } else if (!isMusicType.value) {
            layout()
        }
    }

    // 设置防抖，确保停止调整后执行最后一次（确保最终状态准确）
    layoutTimer = setTimeout(() => {
        lastLayoutTime = Date.now()
        if (initLoading.value && !isMusicType.value) {
            layoutSkeleton()
        } else if (!isMusicType.value) {
            layout()
        }
    }, DEBOUNCE_DELAY)
}

// 监听窗口大小变化
let resizeObserver: ResizeObserver | null = null

// DevTools 切换设备时，window.resize 有时触发时机偏早；用 rAF 等一帧拿到最新的容器宽度
const onViewportResize = () => {
    requestAnimationFrame(() => {
        optimizedLayout()
    })
}

const setupResizeObserver = () => {
    if (!containerRef.value) return

    // 避免重复绑定（initData/切换类型会多次进入）
    if (resizeObserver) {
        resizeObserver.disconnect()
        resizeObserver = null
    }

    resizeObserver = new ResizeObserver(() => {
        // 容器不可见时 offsetWidth 可能为 0，跳过以避免单列闪烁
        if ((containerRef.value?.offsetWidth || 0) > MIN_VISIBLE_WIDTH) {
            optimizedLayout() // 使用优化后的布局函数
        }
    })
    resizeObserver.observe(containerRef.value)
}

onMounted(async () => {
    updateIsMobile()
    window.addEventListener('resize', updateIsMobile)
    // 动态导入并注册 ldrs 组件
    try {
        const { dotSpinner } = await import('ldrs')
        dotSpinner.register()
        // 确保注册完成后再设置 isClient，避免组件在注册前渲染
        isClient.value = true
    } catch (error) {
        console.error('Failed to load ldrs:', error)
        // 即使加载失败也设置为 true，避免无限等待
        isClient.value = true
    }

    // 初始化加载数据（await 确保数据加载完成后再设置滚动监听）
    await initData()

    // 监听视口变化（DevTools 切换设备/旋转/缩放）
    window.addEventListener('resize', onViewportResize)
    window.visualViewport?.addEventListener('resize', onViewportResize)
})

onUnmounted(() => {
    // 停止音乐播放
    musicPlayer.stop()

    window.removeEventListener('resize', updateIsMobile)
    // 清理监听器和定时器
    if (resizeObserver) {
        resizeObserver.disconnect()
        resizeObserver = null
    }
    if (layoutTimer) {
        clearTimeout(layoutTimer)
    }

    // 移除滚动监听
    if (scrollContainer.value) {
        scrollContainer.value.removeEventListener('scroll', throttledScroll)
    }
    if (alsoListenWindow.value) {
        window.removeEventListener('scroll', throttledScroll)
    }
    disconnectLoadMoreObserver()
    videoVisibilityObserver?.disconnect()
    videoVisibilityObserver = null
    videoVisibilityObserverRoot = null
    videoVisibilityTargets.clear()

    if (scrollTimer) {
        clearTimeout(scrollTimer)
    }
    if (mediaRelayoutTimer) {
        clearTimeout(mediaRelayoutTimer)
    }

    window.removeEventListener('resize', onViewportResize)
    window.visualViewport?.removeEventListener('resize', onViewportResize)

    // 恢复 body 滚动
    document.body.style.overflow = ''
    document.body.style.paddingRight = ''
})

// 计算容器总高度（取最高列的高度）
const getWaterfallContainerHeight = (columnHeights: number[]) => {
    const validHeights = columnHeights.filter(
        h => typeof h === 'number' && !isNaN(h)
    )
    return validHeights.length > 0 ? Math.max(...validHeights) : 0
}

const containerHeight = computed(() => {
    return getWaterfallContainerHeight(waterfall.value.columnHeights)
})

const skeletonContainerHeight = computed(() => {
    return getWaterfallContainerHeight(skeletonWaterfall.value.columnHeights)
})

const measuredAspectRatioState = reactive<Record<string, number>>({})

const getMeasuredAspectRatioKey = (item: any) =>
    String(getInspirationId(item) ?? '')

const getMeasuredAspectRatio = (item: any) => {
    const key = getMeasuredAspectRatioKey(item)
    return key ? measuredAspectRatioState[key] : undefined
}

const setMeasuredAspectRatio = (item: any, aspectRatio: number) => {
    const key = getMeasuredAspectRatioKey(item)
    if (!key || !isFinite(aspectRatio) || aspectRatio <= 0) return
    measuredAspectRatioState[key] = aspectRatio
}

// 视频 hover 播放控制（避免把 enter/leave 绑在 <video> 上导致 overlay 抖动）
const warmedVideos = new WeakSet<HTMLVideoElement>()
const pauseTimers = new WeakMap<HTMLVideoElement, ReturnType<typeof setTimeout>>()
const previewReadyState = reactive<Record<string, boolean>>({})
const previewLoadingState = reactive<Record<string, boolean>>({})
const previewPlayingState = reactive<Record<string, boolean>>({})
const lazyVideoSourceState = reactive<Record<string, boolean>>({})
const VIDEO_VISIBILITY_ROOT_MARGIN = '600px 0px'
let videoVisibilityObserver: IntersectionObserver | null = null
let videoVisibilityObserverRoot: HTMLElement | null = null
const videoVisibilityTargets = new Map<string, HTMLElement>()

const isVideoItem = (item: any) =>
    getInspirationType(item.type) === 'VIDEO' ||
    getInspirationOutputType(item.output) === 'VIDEO'

const getPreviewStateKey = (item: any) => String(getInspirationId(item) ?? '')
const getLazyVideoKey = (item: any) => String(getInspirationId(item) ?? '')

const isVideoSourceReadyToLoad = (item: any) => {
    const key = getLazyVideoKey(item)
    return key ? !!lazyVideoSourceState[key] : true
}

const activateVideoSource = (item: any) => {
    const key = getLazyVideoKey(item)
    if (!key) return
    lazyVideoSourceState[key] = true
}

const setupVideoVisibilityObserver = () => {
    if (typeof window === 'undefined') return

    if (!('IntersectionObserver' in window)) {
        videoVisibilityTargets.forEach((_element, key) => {
            lazyVideoSourceState[key] = true
        })
        return
    }

    const nextRoot =
        scrollContainer.value &&
        scrollContainer.value.scrollHeight > scrollContainer.value.clientHeight
            ? scrollContainer.value
            : null

    if (!videoVisibilityObserver || videoVisibilityObserverRoot !== nextRoot) {
        videoVisibilityObserver?.disconnect()
        videoVisibilityObserverRoot = nextRoot
        videoVisibilityObserver = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting) return
                    const key = (entry.target as HTMLElement).dataset.lazyVideoKey
                    if (!key) return
                    lazyVideoSourceState[key] = true
                    videoVisibilityObserver?.unobserve(entry.target)
                    videoVisibilityTargets.delete(key)
                })
            },
            {
                root: nextRoot,
                rootMargin: VIDEO_VISIBILITY_ROOT_MARGIN,
                threshold: 0.01,
            }
        )
    }

    videoVisibilityTargets.forEach((element, key) => {
        if (lazyVideoSourceState[key]) {
            videoVisibilityObserver?.unobserve(element)
            return
        }
        videoVisibilityObserver?.observe(element)
    })
}

const setVideoVisibilityTarget = (element: Element | null, item: any) => {
    if (!isVideoItem(item)) return

    const key = getLazyVideoKey(item)
    if (!key) return

    const previousElement = videoVisibilityTargets.get(key)
    if (previousElement && previousElement !== element) {
        videoVisibilityObserver?.unobserve(previousElement)
    }

    if (!element) {
        if (previousElement) {
            videoVisibilityObserver?.unobserve(previousElement)
        }
        videoVisibilityTargets.delete(key)
        return
    }

    const target = element as HTMLElement
    target.dataset.lazyVideoKey = key
    videoVisibilityTargets.set(key, target)
    setupVideoVisibilityObserver()
}

const isPreviewHovering = (item: any) =>
    hoveredItemId.value === getInspirationId(item)

const syncPreviewReadyState = (item: any, video: HTMLVideoElement | null) => {
    const key = getPreviewStateKey(item)
    if (!key) return
    const isReady = !!video && video.readyState >= 3
    previewReadyState[key] = isReady
    if (isReady) {
        previewLoadingState[key] = false
    }
}

const showPreviewLoadingIcon = (item: any) => {
    const key = getPreviewStateKey(item)
    return (
        isVideoItem(item) &&
        isPreviewHovering(item) &&
        !!previewLoadingState[key] &&
        !previewReadyState[key] &&
        !previewPlayingState[key]
    )
}

const showPreviewPlayIcon = (item: any) => {
    const key = getPreviewStateKey(item)
    return (
        isVideoItem(item) &&
        !isPreviewHovering(item) &&
        !previewPlayingState[key]
    )
}

const handlePreviewVideoLoadedMetadata = (event: Event, item: any) => {
    syncPreviewReadyState(item, event.target as HTMLVideoElement | null)
    onMediaLoad(event, item)
}

const handlePreviewVideoCanPlay = (event: Event, item: any) => {
    syncPreviewReadyState(item, event.target as HTMLVideoElement | null)
}

const handlePreviewVideoPlaying = (item: any) => {
    const key = getPreviewStateKey(item)
    if (!key) return
    previewReadyState[key] = true
    previewLoadingState[key] = false
    previewPlayingState[key] = true
}

const handlePreviewVideoPause = (item: any) => {
    const key = getPreviewStateKey(item)
    if (!key) return
    previewPlayingState[key] = false
    if (!isPreviewHovering(item)) {
        previewLoadingState[key] = false
    }
}

const handlePreviewVideoWaiting = (item: any) => {
    const key = getPreviewStateKey(item)
    if (!key) return
    if (isPreviewHovering(item)) {
        previewLoadingState[key] = true
        previewPlayingState[key] = false
    }
}

const warmUpVideoOnce = (video: HTMLVideoElement) => {
    if (warmedVideos.has(video)) return
    warmedVideos.add(video)
    try {
        // 只提高 preload 等级，不主动调用 load()：
        // load() 可能导致 poster/首帧渲染管线重置，从而在“第一次 hover 播放”时出现闪一下。
        video.preload = 'auto'
    } catch {
        // ignore
    }
}

const playVideoForItem = async (event: MouseEvent, item: any) => {
    if (!isVideoItem(item)) return
    const host = event.currentTarget as HTMLElement | null
    let video = host?.querySelector('video.media-content') as
        | HTMLVideoElement
        | null
    const key = getPreviewStateKey(item)

    if (!isVideoSourceReadyToLoad(item)) {
        activateVideoSource(item)
        if (key) {
            previewLoadingState[key] = true
        }
        await nextTick()
        video = host?.querySelector('video.media-content') as
            | HTMLVideoElement
            | null
    }

    if (!video) return

    const t = pauseTimers.get(video)
    if (t) clearTimeout(t)
    pauseTimers.delete(video)

    warmUpVideoOnce(video)
    if (key) {
        previewLoadingState[key] = video.readyState < 3
        syncPreviewReadyState(item, video)
    }
    if (!video.paused) {
        if (key) {
            previewPlayingState[key] = true
            previewLoadingState[key] = false
        }
        return
    }
    try {
        await video.play()
    } catch (error: any) {
        if (key) {
            previewLoadingState[key] = false
            previewPlayingState[key] = false
        }
        if (error?.name !== 'AbortError') {
            console.error('视频播放失败:', error)
        }
    }
}

const pauseVideoForItem = (event: MouseEvent, item: any) => {
    if (!isVideoItem(item)) return
    const host = event.currentTarget as HTMLElement | null
    const video = host?.querySelector('video.media-content') as
        | HTMLVideoElement
        | null
    if (!video) return
    const key = getPreviewStateKey(item)

    const prev = pauseTimers.get(video)
    if (prev) clearTimeout(prev)
    if (key) {
        previewLoadingState[key] = false
    }
    // 延迟 pause，避免轻微移出/移入导致反复 Abort + 206
    const timer = setTimeout(() => {
        pauseTimers.delete(video)
        if (!video.paused) video.pause()
    }, 250)
    pauseTimers.set(video, timer)
}

// ===== 封装的异步请求函数 =====
const fetchInspirationData = async (page: number = 1) => {
    const params = {
        page: page,
        size: 20,
        type: props.inspirationType,
        types: props.types,
    }

    const res = await getInspirationListApi(params)
    // 容错：过滤掉缺少关键字段导致渲染异常的记录
    const rawList = res?.data?.content || []
    const normalizedList = rawList.map((item: any) => ({
        ...item,
        ins_id: item?.ins_id || item?.id,
    }))
    resData.value = normalizedList.filter((item: any) => {
        const hasOutput =
            item && typeof item.output === 'string' && item.output.trim()
        const hasId = item && getInspirationId(item)
        if (!hasOutput || !hasId) {
            console.warn('跳过无效灵感数据', { item })
            return false
        }
        return true
    })
    total_page.value = res?.data?.total_page || 1
    total_inspiration_data.value = res?.data?.total || 0
}

// 重新加载数据
watch(
    () => props.inspirationType,
    async newV => {
        currentPage.value = 1
        tempData.value = [] // 清空旧数据
        // 使用 await 确保数据加载完成后滚动监听被正确设置
        await initData()
    }
)

// ===== 初始化：加载第一页数据 =====
const initData = async () => {
    initLoading.value = true
    hasMore.value = true
    disconnectLoadMoreObserver()

    if (!isMusicType.value) {
        await nextTick()
        await waitForContainerReady()
        setupResizeObserver()
        layoutSkeleton()
    }

    try {
        await fetchInspirationData(currentPage.value)
        tempData.value = resData.value
    } catch (error) {
        console.error('❌ 加载失败:', error)
    } finally {
        initLoading.value = false

        // 检查是否还有更多数据
        if (currentPage.value >= total_page.value) {
            hasMore.value = false
        }

        // 数据加载完成后，等待 DOM 更新再执行布局
        // 必须在 initLoading=false 之后执行，因为此时瀑布流容器才会渲染
        if (tempData.value.length > 0) {
            await nextTick()
            await waitForWorkbenchInsetStable()
            await waitForContainerReady()
            setupResizeObserver()
            layout()

            // 瀑布流容器渲染后，重新设置滚动监听
            setupScrollListener()
            await ensureScrollableContent()
        }
    }
}

// 遮罩层控制
const showMask = ref(false)
const maskRef = ref<HTMLElement | null>(null)

// 当前遮罩层信息
const currentDetailItem = ref()
// 打开遮罩层
const openMask = (item: any) => {
    console.log(item, '---');
    showMask.value = true
    currentDetailItem.value = item
}

// 监听遮罩层状态，控制 body 滚动（SSR 保护）
watch(
    showMask,
    newVal => {
        if (typeof window === 'undefined' || typeof document === 'undefined')
            return

        if (newVal) {
            // 打开遮罩层，禁止 body 滚动
            const scrollbarWidth =
                window.innerWidth - document.documentElement.clientWidth
            document.body.style.overflow = 'hidden'
            document.body.style.paddingRight = scrollbarWidth + 'px'
        } else {
            // 关闭遮罩层，恢复 body 滚动
            document.body.style.overflow = ''
            document.body.style.paddingRight = ''
        }
    },
    { immediate: true }
)

// 点赞：按作品 ID 防止并发/连点导致状态抖动
const likePendingIds = new Set<string | number>()

const updateLikeFields = (obj: any, hasLike: boolean, likesNum: number) => {
    if (!obj) return
    obj.has_like = hasLike
    obj.likes_num = likesNum
}

const changeLikeStateForItem = async (event: MouseEvent, item: any) => {
    // 检查登录状态
    if (!isLoggedIn.value) {
        showLoginModal()
        return
    }

    const inspirationId = getInspirationId(item)
    if (!inspirationId) return

    // 防止重复点击造成点赞数抖动/并发请求
    if (likePendingIds.has(inspirationId)) return
    likePendingIds.add(inspirationId)

    event?.preventDefault?.()

    // 先做乐观更新：立刻反馈 UI
    const prevHasLike = !!item?.has_like
    const prevLikes = item?.likes_num ?? 0
    const nextHasLike = !prevHasLike
    const nextLikes = nextHasLike ? prevLikes + 1 : Math.max(0, prevLikes - 1)

    // 同步更新：视图列表、原始数据、详情弹窗、以及触发事件的 item 引用（可能是其中之一）
    const viewItem = waterfall.value?.items?.find(
        i => getInspirationId(i) === inspirationId
    )
    const rawItem = tempData.value?.find(i => getInspirationId(i) === inspirationId)
    const detailItem =
        currentDetailItem.value &&
            getInspirationId(currentDetailItem.value) === inspirationId
            ? currentDetailItem.value
            : null

        ;[item, viewItem, rawItem, detailItem].forEach(i =>
            updateLikeFields(i, nextHasLike, nextLikes)
        )

    const params = {
        ins_id: inspirationId,
        like: nextHasLike,
    }

    try {
        const res = await changeUserLikeStateApi(params)

        // 检查接口返回状态
        if (res.status != 200) {
            // 如果状态不是200：回滚乐观更新
            ;[item, viewItem, rawItem, detailItem].forEach(i =>
                updateLikeFields(i, prevHasLike, prevLikes)
            )
            ElMessage.warning(res.message || t('inspiration.operationFailed'))
            return
        }
    } catch (error) {
        console.error('点赞失败:', error)
            // 点赞失败：回滚乐观更新
            ;[item, viewItem, rawItem, detailItem].forEach(i =>
                updateLikeFields(i, prevHasLike, prevLikes)
            )
        ElMessage.error(t('inspiration.likeFailed'))
    } finally {
        likePendingIds.delete(inspirationId)
    }
}

// 分页相关状态
const loading = ref(false)
const initLoading = ref(false)
const hasMore = ref(true)
const currentPage = ref(1)
const isClient = ref(false) // 用于检测是否在客户端
const LOAD_MORE_DISTANCE_THRESHOLD = 300
const MAX_AUTO_LOAD_ROUNDS = 6

// 自动检测滚动容器（可能同时监听多个）
const scrollContainer = ref<HTMLElement | null>(null) // 找到的有 overflow-y: auto 的容器
const alsoListenWindow = ref(false) // 是否同时监听 window
const loadMoreSentinelRef = ref<HTMLElement | null>(null)
let loadMoreObserver: IntersectionObserver | null = null

const disconnectLoadMoreObserver = () => {
    if (loadMoreObserver) {
        loadMoreObserver.disconnect()
        loadMoreObserver = null
    }
}

const setupLoadMoreObserver = async () => {
    disconnectLoadMoreObserver()

    if (
        typeof window === 'undefined' ||
        !('IntersectionObserver' in window)
    ) {
        return
    }

    await nextTick()

    const sentinel = loadMoreSentinelRef.value
    if (!sentinel) return

    loadMoreObserver = new IntersectionObserver(
        entries => {
            if (entries.some(entry => entry.isIntersecting)) {
                void loadMore()
            }
        },
        {
            root: alsoListenWindow.value ? null : scrollContainer.value,
            rootMargin: `0px 0px ${LOAD_MORE_DISTANCE_THRESHOLD}px 0px`,
            threshold: 0.01,
        }
    )

    loadMoreObserver.observe(sentinel)
}

// 获取滚动容器的滚动信息（检查容器和 window，返回更接近底部的那个）
const getScrollInfo = () => {
    const results: { scrollTop: number; scrollHeight: number; clientHeight: number; distanceToBottom: number }[] = []

    // 检查找到的容器
    if (scrollContainer.value) {
        const el = scrollContainer.value
        if (el.scrollHeight > el.clientHeight) {
            const distanceToBottom = el.scrollHeight - (el.scrollTop + el.clientHeight)
            results.push({
                scrollTop: el.scrollTop,
                scrollHeight: el.scrollHeight,
                clientHeight: el.clientHeight,
                distanceToBottom,
            })
        }
    }

    // 检查 window
    if (alsoListenWindow.value) {
        const scrollTop = window.scrollY || document.documentElement.scrollTop
        const scrollHeight = document.documentElement.scrollHeight
        const clientHeight = window.innerHeight
        if (scrollHeight > clientHeight) {
            const distanceToBottom = scrollHeight - (scrollTop + clientHeight)
            results.push({
                scrollTop,
                scrollHeight,
                clientHeight,
                distanceToBottom,
            })
        }
    }

    // 返回距离底部最近的那个
    if (results.length === 0) return null
    return results.reduce((a, b) => (a.distanceToBottom < b.distanceToBottom ? a : b))
}

const isNearBottom = (distanceToBottom: number) =>
    distanceToBottom < LOAD_MORE_DISTANCE_THRESHOLD

// 检查是否应该加载更多
const checkShouldLoadMore = () => {
    if (loading.value || !hasMore.value) return

    const scrollInfo = getScrollInfo()
    if (!scrollInfo) return

    // 距离底部小于300px时触发加载
    if (isNearBottom(scrollInfo.distanceToBottom)) {
        void loadMore()
    }
}

// 加载更多数据（追加数据，显示 loading）
const loadMore = async () => {
    if (loading.value || !hasMore.value) return

    loading.value = true
    const nextPage = currentPage.value + 1

    try {
        const previousLength = tempData.value.length
        await fetchInspirationData(nextPage)
        const nextPageItems = [...resData.value]
        // 追加数据而不是替换，实现真正的"加载更多"
        tempData.value = [...tempData.value, ...nextPageItems]
        currentPage.value = nextPage

        // 数据加载完成后，执行布局
        await nextTick()
        appendLayout(nextPageItems, previousLength)
        setupScrollListener()

        if (currentPage.value >= total_page.value) {
            hasMore.value = false
        }
    } catch (error) {
        console.error('加载失败:', error)
    } finally {
        loading.value = false
    }
}

// 节流处理滚动事件
let scrollTimer: ReturnType<typeof setTimeout> | null = null
const throttledScroll = () => {
    const scrollInfo = getScrollInfo()
    if (scrollInfo) {
        emit('scroll-state', {
            scrollTop: scrollInfo.scrollTop,
            distanceFromBottom: scrollInfo.distanceToBottom,
        })
    }

    if (scrollTimer) return

    scrollTimer = setTimeout(() => {
        checkShouldLoadMore()
        scrollTimer = null
    }, 150)
}

// 查找最近的可滚动父容器（返回有 overflow-y: auto/scroll 的元素，如果没找到返回 null）
function findScrollContainer(element: HTMLElement | null): HTMLElement | null {
    if (!element) return null

    let parent = element.parentElement
    // 用于存储第一个"潜在"的滚动容器（有 overflow-y: auto/scroll 但内容暂时没有超出）
    let potentialContainer: HTMLElement | null = null

    while (parent) {
        const style = window.getComputedStyle(parent)
        const overflowY = style.overflowY

        // 找到第一个可滚动的父容器
        if (overflowY === 'auto' || overflowY === 'scroll') {
            // 如果内容已经超出容器高度，立即返回
            if (parent.scrollHeight > parent.clientHeight) {
                return parent
            }
            // 否则记录为潜在容器（内容加载后可能会变成真正的滚动容器）
            if (!potentialContainer) {
                potentialContainer = parent
            }
        }

        parent = parent.parentElement
    }

    // 如果没找到实际滚动的容器，但有潜在容器，返回潜在容器
    if (potentialContainer) {
        return potentialContainer
    }

    return null
}

// ===== 统一的滚动监听设置函数 =====
const setupScrollListener = () => {
    // 先移除旧的监听器
    if (scrollContainer.value) {
        scrollContainer.value.removeEventListener('scroll', throttledScroll)
    }
    if (alsoListenWindow.value) {
        window.removeEventListener('scroll', throttledScroll)
    }

    // 重新检测滚动容器
    const foundContainer = findScrollContainer(containerRef.value)
    scrollContainer.value = foundContainer

    // 策略：同时监听找到的容器和 window
    // 因为不同页面的滚动可能发生在不同的地方
    if (foundContainer) {
        foundContainer.addEventListener('scroll', throttledScroll)
    }

    // 同时监听 window（除非找到的容器已经是可滚动的）
    const containerCanScroll = foundContainer && foundContainer.scrollHeight > foundContainer.clientHeight
    alsoListenWindow.value = !containerCanScroll
    if (alsoListenWindow.value) {
        window.addEventListener('scroll', throttledScroll)
    }

    void setupLoadMoreObserver()
    setupVideoVisibilityObserver()
}

const shouldAutoLoadMore = () => {
    if (loading.value || !hasMore.value) return false

    const containerEl = scrollContainer.value
    if (containerEl) {
        const remainingScrollableDistance =
            containerEl.scrollHeight - containerEl.clientHeight
        return remainingScrollableDistance <= LOAD_MORE_DISTANCE_THRESHOLD
    }

    if (alsoListenWindow.value) {
        const remainingScrollableDistance =
            document.documentElement.scrollHeight - window.innerHeight
        return remainingScrollableDistance <= LOAD_MORE_DISTANCE_THRESHOLD
    }

    const scrollInfo = getScrollInfo()
    if (!scrollInfo) {
        return true
    }

    return scrollInfo.scrollHeight - scrollInfo.clientHeight <=
        LOAD_MORE_DISTANCE_THRESHOLD
}

// 刷新/首屏直达时，列表可能尚未形成足够的滚动余量；主动补齐少量页数直到可顺畅滚动。
const ensureScrollableContent = async () => {
    let rounds = 0

    while (rounds < MAX_AUTO_LOAD_ROUNDS && shouldAutoLoadMore()) {
        rounds++
        await loadMore()
    }
}

// 导航跳转
const navigationChange = async (item: any) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return

    const id = getInspirationId(item)
    let payload = item
    if (id) {
        try {
            const res = await getInspirationDetailApi(String(id))
            if (res?.status === 200 && res?.data) {
                payload = res.data
            }
        } catch (e) {
            console.error('做同款前灵感详情加载失败，使用列表数据:', e)
        }
    }

    // Chat page: do NOT navigate; only prefill PcWorkbench and stay on inspiration tab.
    if (isInChatPageRuntime()) {
        const prefill = buildWorkbenchPrefillFromInspirationItem(payload)
        if (prefill) {
            emitter.emit('workbench-apply-prefill', prefill)
        }
        return
    }

    const targetPath = formatPath(
        payload.type,
        payload.param?.template_id,
        payload.param?.template_path
    )
    if (targetPath === 'noo') {
        ElMessage.warning(t('inspiration.operationFailed'))
        return
    }

    // Tool pages: keep original behavior (navigate + tool-page rewrite),
    // and also prefill the (now-present) PcWorkbench.
    const prefill = buildWorkbenchPrefillFromInspirationItem(payload)
    if (prefill) {
        writeWorkbenchInspirationPrefillToStorage(prefill)
        // PcWorkbench is typically a persistent component on tool pages (not remounted on route change),
        // so also apply prefill immediately via event bus to ensure the workbench panel updates.
        emitter.emit('workbench-apply-prefill', prefill)
    }

    const timestamp = Date.now()
    // const targetPath = item.type
    const path = `/${targetPath}?customType=inspiration&t=${timestamp}`

    const dataWriteBack = JSON.stringify(payload)
    localStorage.setItem('inspirationData', dataWriteBack)
    const inspirationId = getInspirationId(payload)
    if (inspirationId) {
        localStorage.setItem('inspirationInsId', String(inspirationId))
    }
    sendEffectsDetailsEvent(payload?.id)
    navigateTo(path)
}

// 动态显示创建内容和其他功能
const hoveredItemId = ref(null)

// ==================== 悬浮工具功能 ====================
// 添加图片到工作台输入框
const handleAddToInput = (item: any) => {
    // ==================== 埋点：点击添加到输入框 icon ====================
    analyticsEvent('click_add_to_input_icon')

    const imageUrl = item.output
    if (!imageUrl) {
        console.warn('No image URL provided')
        return
    }

    // 判断左侧创作面板是否打开，且不在 chat 路由下
    const isInChatPage = route.path.includes('/chat')
    if (!appStore.creatorPanelCollapsed && !isInChatPage) {
        // 创作面板已打开，且不在 chat 页面，执行 tab 切换和填充逻辑
        handleAddToInputWithTabSwitch(item)
        return
    }

    // 无论创作面板是否打开，都要同步到输入框
    // PcWorkbench 会根据当前模式自动处理：
    // - 文生图 → 自动切换到图生图
    // - 图生图/角色生图 → 检查上限并添加
    // - 文生视频 → 自动切换到图生视频
    // - 图生视频/角色生视频 → 检查上限并添加
    // - 图片模板/视频模板 → 添加（最多2张）
    // - 图片工具 → 添加并触发弹窗
    // - 视频工具 → Toast 提示错误
    emitter.emit('add-image-to-workbench-input', { imageUrl })
}

// 添加视频到工作台输入框
const handleAddVideoToInput = (item: any) => {
    const videoUrl = item.output
    if (!videoUrl) {
        console.warn('No video URL provided')
        return
    }

    // 判断左侧创作面板是否打开，且不在 chat 路由下
    const isInChatPage = route.path.includes('/chat')
    if (!appStore.creatorPanelCollapsed && !isInChatPage) {
        // 创作面板已打开，且不在 chat 页面，跳转到视频编辑并填充
        handleAddVideoWithTabSwitch(item)
        return
    }

    // 无论创作面板是否打开，都要同步到输入框
    // PcWorkbench 会根据当前模式自动处理：
    // - 视频工具 → 添加到工具视频字段
    // - 其他模式 → 自动切换到视频编辑模式
    emitter.emit('add-video-to-workbench-input', { videoUrl })
}

// 处理图片添加（带 tab 切换）- 左侧创作面板打开时使用
const handleAddToInputWithTabSwitch = (item: any) => {
    const imageUrl = item.output

    // 获取当前路由，判断目标页面
    const currentPath = route.path
    let targetPath = ''
    let needNavigate = false

    // 判断目标页面和是否需要跳转
    if (
        currentPath === '/text-to-image' ||
        currentPath.includes('text-to-image')
    ) {
        // 文生图 → 需要跳转到图生图
        targetPath = '/image-to-image'
        needNavigate = true
    } else if (
        currentPath === '/image-to-image' ||
        currentPath.includes('image-to-image')
    ) {
        // 图生图 → 不跳转，直接添加
        targetPath = '/image-to-image'
        needNavigate = false
    } else if (
        currentPath.includes('/character-to-image') ||
        currentPath.includes('character')
    ) {
        // 角色生图 → 不跳转，直接添加
        targetPath = currentPath
        needNavigate = false
    } else if (currentPath.includes('/photo-effects/')) {
        // 图片特效 → 不跳转，直接添加
        targetPath = currentPath
        needNavigate = false
    } else {
        // 其他情况，跳转到图生图
        targetPath = '/image-to-image'
        needNavigate = true
    }

    // 🔥 每次只传单个图片，目标页面会 push 到现有数组中
    const inspirationData = {
        output: imageUrl,
        param: {
            pic_urls: [imageUrl], // 每次只传单个图片
        },
        type: item.type,
        targetPage: targetPath, // 记录目标页面
        fillImageOnly: true, // 🔥 标识：只填充图片，不填充其他参数
    }
    localStorage.setItem('inspirationData', JSON.stringify(inspirationData))

    if (needNavigate) {
        // 需要跳转：跳转到目标页面
        // 🔥 使用 addImageFromInspiration 而不是 inspiration，避免影响右侧历史数据加载
        navigateTo({
            path: targetPath,
            query: {
                customType: 'addImageFromInspiration',
                t: Date.now(),
            },
        })
    } else {
        // 不需要跳转：更新当前页面的 query 参数来触发 watch
        navigateTo({
            path: currentPath,
            query: {
                ...route.query, // 保留现有query参数
                customType: 'addImageFromInspiration',
                t: Date.now(),
            },
            replace: true, // 不增加历史记录
        })
    }
}

// 处理视频添加（带 tab 切换）- 左侧创作面板打开时使用
const handleAddVideoWithTabSwitch = (item: any) => {
    const videoUrl = item.output
    const targetPath = '/video-to-video'

    // 准备 localStorage 数据（只保存视频 URL，不保存其他参数）
    const inspirationData = {
        output: videoUrl,
        type: item.type,
        targetPage: targetPath, // 记录目标页面
        fillImageOnly: true, // 🔥 标识：只填充视频，不填充其他参数
    }
    localStorage.setItem('inspirationData', JSON.stringify(inspirationData))

    // 跳转到视频编辑页面
    // 🔥 使用 addImageFromInspiration 而不是 inspiration，避免影响右侧历史数据加载
    navigateTo({
        path: targetPath,
        query: {
            customType: 'addImageFromInspiration',
            t: Date.now(),
        },
    })
}

// 打开图片编辑工具弹窗
const handleOpenImageToolModal = (imageUrl: string) => {
    if (!imageUrl) {
        console.warn('No image URL provided')
        return
    }
    emitter.emit('open-tool-modal-from-history', {
        imageUrl,
        type: 'IMAGE'
    })
}

// 打开图片模板特效弹窗
const handleOpenImageTemplateModal = (imageUrl: string) => {
    if (!imageUrl) {
        console.warn('No image URL provided')
        return
    }
    emitter.emit('open-template-modal-from-history', {
        imageUrl,
        type: 'IMAGE'
    })
}

// 打开视频模板特效弹窗
const handleOpenVideoTemplateModal = (imageUrl: string) => {
    if (!imageUrl) {
        console.warn('No image URL provided')
        return
    }
    emitter.emit('open-template-modal-from-history', {
        imageUrl,
        type: 'VIDEO'
    })
}

// 打开视频编辑工具弹窗
const handleOpenVideoToolModal = (videoUrl: string) => {
    if (!videoUrl) {
        console.warn('No video URL provided')
        return
    }
    emitter.emit('open-tool-modal-from-history', {
        imageUrl: videoUrl,
        type: 'VIDEO'
    })
}

// Tooltip 样式 - 和 BatchOutputSide 保持一致
const popperStyle =
    'font-size:13px;color:#fff;background: #26262a;border:none;z-index:10;box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 12px;'
</script>

<template style="position: relative">
    <div class="inspiration-root">
        <!-- 初始加载状态 -->
        <div
            v-if="initLoading"
            class="loading-state"
            :class="{ 'loading-state--waterfall': !isMusicType }"
        >
            <div v-if="isMusicType" class="music-skeleton-grid" aria-hidden="true">
                <div v-for="item in musicSkeletonCards" :key="`music-skeleton-${item}`" class="music-skeleton-card">
                    <div class="music-skeleton-badge"></div>
                    <div class="music-skeleton-vinyl"></div>
                    <div class="music-skeleton-line music-skeleton-line--title"></div>
                    <div class="music-skeleton-user-row">
                        <div class="music-skeleton-avatar"></div>
                        <div class="music-skeleton-line music-skeleton-line--user"></div>
                        <div class="music-skeleton-like"></div>
                    </div>
                </div>
            </div>
            <div
                v-else
                ref="containerRef"
                class="waterfall-container waterfall-skeleton-container"
                :class="{ 'waterfall-container--tool-page-pc': isToolPagePcUiVariant }"
                :style="{ height: `${skeletonContainerHeight}px` }"
                aria-hidden="true"
            >
                <div
                    v-for="item in skeletonWaterfall.items"
                    :key="`inspiration-skeleton-${item.id}`"
                    class="waterfall-item waterfall-skeleton-item"
                    :class="{ 'waterfall-item--tool-page-pc': isToolPagePcUiVariant }"
                    :style="item.style"
                ></div>
            </div>
        </div>

        <!-- ==================== 音乐类型：网格布局 + 黑胶卡片 ==================== -->
        <template v-else-if="isMusicType">
            <!-- 音乐卡片网格 - 有数据时显示 -->
            <div v-if="tempData.length > 0" ref="containerRef" class="music-grid-container">
                <div v-for="item in tempData" :key="getInspirationId(item)" class="music-card cursor-pointer"
                    @click="openMusicMask(item)">
                    <!-- 背景层：图片 + 高斯模糊 -->
                    <div class="music-card-bg" :style="{ backgroundImage: `url(${item.output})` }"></div>

                    <!-- 左上角类型标签 -->
                    <div class="music-type-tag">
                        <img src="~/assets/images/common/music-type.png"
                            srcset="~/assets/images/common/music-type.png 1x, ~/assets/images/common/music-type@2x.png 2x"
                            alt="music" aria-hidden="true" class="w-[9px] h-[9px]" width="9" height="9" />
                        <span>{{ getMusicTypeLabel(item) }}</span>
                    </div>

                    <!-- 黑胶唱片 -->
                    <div class="vinyl-record" :class="{
                        spinning: musicPlayer.isCurrentPlaying(
                            getInspirationId(item)
                        ),
                    }">
                        <div class="vinyl-outer"></div>
                        <div class="vinyl-inner">
                            <img :src="item.output" :alt="item.music?.music_title || 'Music cover'"
                                class="vinyl-cover" />
                            <div class="vinyl-center-dot"></div>
                        </div>
                    </div>

                    <!-- 常态信息区：歌曲名 + 用户信息 -->
                    <div class="info-section">
                        <!-- 歌曲名称 -->
                        <div class="song-title-row">
                            <div class="song-title">
                                {{
                                    item.music?.music_title ||
                                    item.param?.prompt ||
                                    t('inspiration.untitledMusic')
                                }}
                            </div>
                            <!-- <span class="song-type-tag">{{ getMusicTypeLabel(item) }}</span> -->
                        </div>
                        <!-- 用户信息行 -->
                        <div class="user-info-row">
                            <div class="user-left">
                                <img :src="item.user?.avatar ||
                                    'https://static.weryai.com/growth/image/9ead8a3f9b5dd9d44367d3aba6ac8a93.webp'
                                    " :alt="item.user?.user_name ||
                                        t('inspiration.unknownUser')
                                        " class="user-avatar" />
                                <span class="user-name">{{
                                    item.user?.user_name ||
                                    t('inspiration.unknownUser')
                                    }}</span>
                            </div>
                            <button type="button" class="like-btn" :aria-label="item.has_like
                                ? `${t('common.unlike')} ${item.music?.music_title || item.param?.prompt || t('inspiration.untitledMusic')}`
                                : `${t('common.like')} ${item.music?.music_title || item.param?.prompt || t('inspiration.untitledMusic')}`
                                " @click.stop="
                                    changeLikeStateForItem($event, item)
                                    ">
                                <svg v-if="!item.has_like" xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                                    viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                    <path
                                        d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                        stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                        stroke-linejoin="round" fill="none" />
                                </svg>
                                <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                                    viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                    <path
                                        d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                        fill="#FF5353" />
                                </svg>
                                <span aria-hidden="true">{{
                                    formatLikeCount(item.likes_num)
                                    }}</span>
                            </button>
                        </div>
                    </div>

                    <!-- Hover 覆盖层：歌词 + 操作按钮 -->
                    <div class="hover-overlay">
                        <!-- 歌词滚动区域 -->
                        <div class="lyrics-area">
                            <p class="lyrics-text">
                                {{
                                    item.music?.generated_lyrics ||
                                    item.param?.lyrics ||
                                    t('inspiration.noLyrics')
                                }}
                            </p>
                        </div>
                        <!-- 操作按钮行 -->
                        <div class="action-row">
                            <button type="button" class="play-btn" :aria-label="musicPlayer.isCurrentLoading(
                                getInspirationId(item)
                            )
                                ? t('common.loading')
                                : musicPlayer.isCurrentPlaying(
                                    getInspirationId(item)
                                )
                                    ? `${t('common.pause')} ${item.music?.music_title || item.param?.prompt || t('inspiration.untitledMusic')}`
                                    : `${t('common.play')} ${item.music?.music_title || item.param?.prompt || t('inspiration.untitledMusic')}`
                                " @click.stop="togglePlay(item)">
                                <!-- 加载中图标 -->
                                <svg v-if="
                                    musicPlayer.isCurrentLoading(
                                        getInspirationId(item)
                                    )
                                " class="animate-spin" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                    viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" stroke="white" stroke-width="2"
                                        stroke-opacity="0.3" />
                                    <path d="M12 2a10 10 0 0 1 10 10" stroke="white" stroke-width="2"
                                        stroke-linecap="round" />
                                </svg>
                                <!-- 播放图标 -->
                                <svg v-else-if="
                                    !musicPlayer.isCurrentPlaying(
                                        getInspirationId(item)
                                    )
                                " xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                    fill="white" aria-hidden="true">
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                                <!-- 暂停图标 -->
                                <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                    viewBox="0 0 24 24" fill="white" aria-hidden="true">
                                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                                </svg>
                            </button>
                            <button type="button" class="make-similar-btn"
                                :aria-label="`${t('menus.aiMusicPage.makeSimilar')} ${item.music?.music_title || item.param?.prompt || t('inspiration.untitledMusic')}`"
                                @click.stop="handleMusicMakeSimilar(item)">
                                <img src="~/assets/images/common/left-music-icon.png" alt="music" aria-hidden="true"
                                    class="w-[14px] h-[14px]" width="14" height="14" />
                                <span aria-hidden="true">{{ t('menus.aiMusicPage.makeSimilar') }}</span>
                            </button>
                            <button type="button" class="hover-like-btn" :aria-label="item.has_like
                                ? `${t('common.unlike')} ${item.music?.music_title || item.param?.prompt || t('inspiration.untitledMusic')}`
                                : `${t('common.like')} ${item.music?.music_title || item.param?.prompt || t('inspiration.untitledMusic')}`
                                " @click.stop="changeLikeStateForItem($event, item)">
                                <svg v-if="!item.has_like" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                    viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                    <path
                                        d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                        stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
                                        fill="none" />
                                </svg>
                                <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                    viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                    <path
                                        d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                        fill="#FF5353" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 空数据时显示 -->
            <div v-else class="empty-state">
                <img class="w-full max-w-[300px] h-auto aspect-[300/208] object-contain"
                    src="https://static.weryai.com/growth/image/48b2bf9f0fcba0cdd7c00550a00b978b.webp"
                    alt="empty-logo" />
                <div class="text-base text-text-adad mt-5">
                    {{ t('inspiration.noInspirationData') }}
                </div>
            </div>

            <!-- 底部加载更多状态 -->
            <div
                v-if="tempData.length > 0"
                ref="loadMoreSentinelRef"
                class="load-more-sentinel"
                aria-hidden="true"
            ></div>
            <div v-if="loading && !initLoading && tempData.length > 0" class="loading-indicator">
                <l-tailspin size="20" stroke="3" speed="0.9" color="white" class="mx-1" aria-hidden="true" />
            </div>

            <!-- 没有更多数据提示 -->
            <div v-if="!hasMore && tempData.length > 0 && !loading" class="no-more-indicator">
                {{ t('inspiration.noMoreData') }}
            </div>
        </template>

        <!-- ==================== 图片/视频类型：瀑布流布局 ==================== -->
        <template v-else>
            <!-- 瀑布流容器 - 有数据时显示 -->
            <div v-if="total_inspiration_data > 0" class="waterfall-container"
                :class="{ 'waterfall-container--tool-page-pc': isToolPagePcUiVariant }" ref="containerRef"
                :style="{ height: `${containerHeight}px` }">
                <!-- 瀑布流元素 -->
                <div v-for="item in waterfall.items" :key="getInspirationId(item)" class="waterfall-item"
                    :class="{ 'waterfall-item--tool-page-pc': isToolPagePcUiVariant }"
                    :style="item.style" @click="openMask(item)"
                    @mouseenter="(e) => { hoveredItemId = getInspirationId(item); playVideoForItem(e as any, item) }"
                    @mouseleave="(e) => { hoveredItemId = null; pauseVideoForItem(e as any, item) }">
                    <!-- 内部包裹层，用于相对定位 -->
                    <div class="item-wrapper group"
                        :class="{ 'item-wrapper--tool-page-pc': isToolPagePcUiVariant }"
                        :ref="el => setVideoVisibilityTarget(el as Element | null, item)">
                        <!-- 视频类型 -->
                        <video v-if="
                            getInspirationType(item.type) === 'VIDEO' ||
                            getInspirationOutputType(item.output) === 'VIDEO'
                        " :src="isVideoSourceReadyToLoad(item) ? item.output : undefined" class="media-content"
                            :poster="getResourceThumbnail(item.output)" muted loop playsinline
                            :preload="isVideoSourceReadyToLoad(item) ? 'metadata' : 'none'"
                            @loadedmetadata="handlePreviewVideoLoadedMetadata($event, item)"
                            @canplay="handlePreviewVideoCanPlay($event, item)"
                            @canplaythrough="handlePreviewVideoCanPlay($event, item)"
                            @playing="handlePreviewVideoPlaying(item)" @pause="handlePreviewVideoPause(item)"
                            @waiting="handlePreviewVideoWaiting(item)" disablepictureinpicture
                            @contextmenu.prevent />

                        <!-- 图片类型 -->
                        <img v-else-if="
                            getInspirationType(item.type) == 'IMAGE' ||
                            getInspirationOutputType(item.output) === 'IMAGE'
                        " :src="item.output" class="media-content" :alt="item.user?.user_name
                            ? `${item.user.user_name}'s AI generated image`
                            : 'AI generated image'
                            " loading="lazy" decoding="async" @load="onMediaLoad($event, item)" />

                        <!-- 图片的 Hover 操作区（右上角，4个图标） -->
                        <div v-if="!isAiToolRoute && (getInspirationType(item.type) === 'IMAGE' || getInspirationOutputType(item.output) === 'IMAGE')"
                            class="waterfall-hover-tools absolute z-10 top-2 right-2 flex items-center justify-center gap-[14px] rounded-[6px] p-[10px] w-max">
                            <!-- 添加到输入框(同时触发做同款) -->
                            <el-tooltip :content="t('workbench.addToInput')" popper-class="prompt-popper"
                                :popper-style="popperStyle">
                                <span
                                    class="flex items-center justify-center hover:scale-110 cursor-pointer transition-all duration-300"
                                    @click.stop="handleAddToInput(item)">
                                    <span class="i-upload-plus-icon w-4 h-4 text-white"></span>
                                </span>
                            </el-tooltip>

                            <!-- 图片编辑工具 -->
                            <el-tooltip :content="t('workbench.imageEdit')" popper-class="prompt-popper"
                                :popper-style="popperStyle">
                                <span
                                    class="flex items-center justify-center hover:scale-110 cursor-pointer transition-all duration-300"
                                    @click.stop="handleOpenImageToolModal(item.output)">
                                    <span class="i-image-edit-icon w-4 h-4 text-white"></span>
                                </span>
                            </el-tooltip>

                            <!-- 图片模板特效 -->
                            <el-tooltip :content="t('templateModal.imageEffects')" popper-class="prompt-popper"
                                :popper-style="popperStyle">
                                <span
                                    class="flex items-center justify-center hover:scale-110 cursor-pointer transition-all duration-300"
                                    @click.stop="handleOpenImageTemplateModal(item.output)">
                                    <img src="@/assets/images/menu/photo-effects.png" alt="effects" class="w-4 h-4" />
                                </span>
                            </el-tooltip>

                            <!-- 视频模板特效 -->
                            <el-tooltip :content="t('templateModal.videoEffects')" popper-class="prompt-popper"
                                :popper-style="popperStyle">
                                <span
                                    class="flex items-center justify-center hover:scale-110 cursor-pointer transition-all duration-300"
                                    @click.stop="handleOpenVideoTemplateModal(item.output)">
                                    <img src="@/assets/images/menu/video-effects.png" alt="video-effects"
                                        class="w-4 h-4" />
                                </span>
                            </el-tooltip>
                        </div>

                        <!-- 视频的 Hover 操作区（右上角，添加到输入框 + 视频编辑按钮） -->
                        <div v-else-if="!isAiToolRoute && (getInspirationType(item.type) === 'VIDEO' || getInspirationOutputType(item.output) === 'VIDEO')"
                            class="waterfall-hover-tools absolute z-10 top-2 right-2 flex items-center justify-center gap-[14px] rounded-[6px] p-[10px] w-max">
                            <!-- 添加到输入框 -->
                            <el-tooltip :content="t('workbench.addToInput')" popper-class="prompt-popper"
                                :popper-style="popperStyle">
                                <span
                                    class="flex items-center justify-center hover:scale-110 cursor-pointer transition-all duration-300"
                                    @click.stop="handleAddVideoToInput(item)">
                                    <span class="i-upload-plus-icon w-4 h-4 text-white"></span>
                                </span>
                            </el-tooltip>

                            <!-- 视频编辑工具 -->
                            <el-tooltip :content="t('workbench.videoEdit')" popper-class="prompt-popper"
                                :popper-style="popperStyle">
                                <span
                                    class="flex items-center justify-center hover:scale-110 cursor-pointer transition-all duration-300"
                                    @click.stop="handleOpenVideoToolModal(item.output)">
                                    <span class="i-video-edit-icon w-4 h-4 text-white"></span>
                                </span>
                            </el-tooltip>
                        </div>

                        <div v-if="isVideoItem(item) && (showPreviewPlayIcon(item) || showPreviewLoadingIcon(item))"
                            class="hidden lg:flex absolute top-3 left-3 z-[2] size-5 items-center justify-center transition-opacity duration-200">
                            <svg v-if="showPreviewPlayIcon(item)" width="20" height="20" viewBox="0 0 36 36"
                                fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <circle cx="18" cy="18" r="18" fill="black" fill-opacity="0.35" />
                                <path
                                    d="M16.1824 10.9008C14.6834 9.97494 12.75 11.0532 12.75 12.8151V23.186C12.75 24.9479 14.6833 26.0262 16.1824 25.1004L24.5779 19.9149C26.0014 19.0356 26.0014 16.9655 24.5779 16.0863L16.1824 10.9008Z"
                                    fill="white" />
                            </svg>

                            <svg v-else class="preview-loading-spinner" width="20" height="20" viewBox="0 0 36 36"
                                fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <circle cx="18" cy="18" r="18" fill="black" fill-opacity="0.35" />
                                <path
                                    d="M11.5348 12.5599C11.5348 13.1971 11.7879 13.8082 12.2385 14.2588C12.689 14.7093 13.3001 14.9624 13.9373 14.9624C14.5745 14.9624 15.1856 14.7093 15.6362 14.2588C16.0867 13.8082 16.3399 13.1971 16.3399 12.5599C16.3399 12.2444 16.2777 11.932 16.157 11.6405C16.0362 11.349 15.8593 11.0841 15.6362 10.861C15.4131 10.6379 15.1482 10.461 14.8567 10.3402C14.5652 10.2195 14.2528 10.1573 13.9373 10.1573C13.6218 10.1573 13.3094 10.2195 13.0179 10.3402C12.7264 10.461 12.4615 10.6379 12.2385 10.861C12.0154 11.0841 11.8384 11.349 11.7176 11.6405C11.5969 11.932 11.5348 12.2444 11.5348 12.5599ZM9.73341 17.6647C9.73261 17.9412 9.78639 18.2152 9.89166 18.4709C9.99693 18.7267 10.1516 18.9591 10.3469 19.1549C10.5422 19.3507 10.7742 19.5061 11.0296 19.6121C11.285 19.7181 11.5588 19.7727 11.8354 19.7727C12.1119 19.7727 12.3857 19.7181 12.6411 19.6121C12.8966 19.5061 13.1285 19.3507 13.3238 19.1549C13.5191 18.9591 13.6738 18.7267 13.7791 18.4709C13.8843 18.2152 13.9381 17.9412 13.9373 17.6647C13.9381 17.3881 13.8843 17.1142 13.7791 16.8584C13.6738 16.6027 13.5191 16.3703 13.3238 16.1744C13.1285 15.9786 12.8966 15.8232 12.6411 15.7172C12.3857 15.6112 12.1119 15.5566 11.8354 15.5566C11.5588 15.5566 11.285 15.6112 11.0296 15.7172C10.7742 15.8232 10.5422 15.9786 10.3469 16.1744C10.1516 16.3703 9.99693 16.6027 9.89166 16.8584C9.78639 17.1142 9.73261 17.3881 9.73341 17.6647ZM11.5352 22.1692C11.5352 22.6471 11.725 23.1053 12.0629 23.4432C12.4008 23.7812 12.8591 23.971 13.337 23.971C13.8149 23.971 14.2732 23.7812 14.6111 23.4432C14.949 23.1053 15.1388 22.6471 15.1388 22.1692C15.1388 21.6913 14.949 21.233 14.6111 20.8951C14.2732 20.5572 13.8149 20.3674 13.337 20.3674C12.8591 20.3674 12.4008 20.5572 12.0629 20.8951C11.725 21.233 11.5352 21.6913 11.5352 22.1692ZM16.6499 24.1514C16.6499 24.5691 16.8158 24.9698 17.1112 25.2651C17.4066 25.5605 17.8072 25.7264 18.2249 25.7264C18.6426 25.7264 19.0432 25.5605 19.3386 25.2651C19.634 24.9698 19.7999 24.5691 19.7999 24.1514C19.7999 23.7337 19.634 23.3331 19.3386 23.0377C19.0432 22.7424 18.6426 22.5764 18.2249 22.5764C17.8072 22.5764 17.4066 22.7424 17.1112 23.0377C16.8158 23.3331 16.6499 23.7337 16.6499 24.1514ZM21.7448 22.4698C21.7448 22.8681 21.903 23.25 22.1846 23.5316C22.4662 23.8133 22.8482 23.9715 23.2465 23.9715C23.6447 23.9715 24.0267 23.8133 24.3083 23.5316C24.5899 23.25 24.7481 22.8681 24.7481 22.4698C24.7481 22.0715 24.5899 21.6896 24.3083 21.408C24.0267 21.1264 23.6447 20.9681 23.2465 20.9681C22.8482 20.9681 22.4662 21.1264 22.1846 21.408C21.903 21.6896 21.7448 22.0715 21.7448 22.4698ZM24.1478 17.3645C24.1478 17.6831 24.2743 17.9886 24.4996 18.2138C24.7248 18.439 25.0303 18.5656 25.3489 18.5656C25.6674 18.5656 25.9729 18.439 26.1981 18.2138C26.4234 17.9886 26.5499 17.6831 26.5499 17.3645C26.5499 17.046 26.4234 16.7405 26.1981 16.5153C25.9729 16.29 25.6674 16.1635 25.3489 16.1635C25.0303 16.1635 24.7248 16.29 24.4996 16.5153C24.2743 16.7405 24.1478 17.046 24.1478 17.3645ZM22.3456 12.86C22.3456 12.9783 22.3689 13.0955 22.4142 13.2048C22.4594 13.3141 22.5258 13.4134 22.6094 13.4971C22.6931 13.5807 22.7924 13.6471 22.9017 13.6924C23.011 13.7376 23.1282 13.7609 23.2465 13.7609C23.3648 13.7609 23.4819 13.7376 23.5912 13.6924C23.7005 13.6471 23.7998 13.5807 23.8835 13.4971C23.9672 13.4134 24.0335 13.3141 24.0788 13.2048C24.1241 13.0955 24.1474 12.9783 24.1474 12.86C24.1474 12.6211 24.0524 12.392 23.8835 12.223C23.7145 12.054 23.4854 11.9591 23.2465 11.9591C23.0075 11.9591 22.7784 12.054 22.6094 12.223C22.4405 12.392 22.3456 12.6211 22.3456 12.86ZM17.9999 10.5011C17.9999 10.58 18.0154 10.6582 18.0456 10.731C18.0758 10.8039 18.1201 10.8702 18.1759 10.9259C18.2316 10.9817 18.2979 11.026 18.3708 11.0562C18.4436 11.0864 18.5218 11.1019 18.6007 11.1019C18.6796 11.1019 18.7577 11.0864 18.8306 11.0562C18.9034 11.026 18.9697 10.9817 19.0255 10.9259C19.0812 10.8702 19.1255 10.8039 19.1557 10.731C19.1859 10.6582 19.2014 10.58 19.2014 10.5011C19.2014 10.4222 19.1859 10.3441 19.1557 10.2712C19.1255 10.1984 19.0812 10.1321 19.0255 10.0763C18.9697 10.0206 18.9034 9.97631 18.8306 9.94612C18.7577 9.91593 18.6796 9.90039 18.6007 9.90039C18.5218 9.90039 18.4436 9.91593 18.3708 9.94612C18.2979 9.97631 18.2316 10.0206 18.1759 10.0763C18.1201 10.1321 18.0758 10.1984 18.0456 10.2712C18.0154 10.3441 17.9999 10.4222 17.9999 10.5011Z"
                                    fill="white" />
                            </svg>
                        </div>

                        <div v-if="isVideoItem(item)"
                            class="hidden lg:block absolute inset-0 z-[1] pointer-events-none opacity-0 transition-opacity duration-300"
                            :class="{ 'preview-overlay-visible': hoveredItemId === getInspirationId(item) }"
                            style="background: rgba(0, 0, 0, 0.1)"></div>

                        <!-- 内容信息层 -->
                        <div class="item-info z-[2]" :data-id="getInspirationId(item)" @click.stop>
                            <div class="left-info">
                                <div class="user-info">
                                    <img v-if="item.user?.avatar" :src="item.user?.avatar" :alt="item.user?.user_name
                                        ? `${item.user.user_name}'s avatar`
                                        : 'User avatar'
                                        " class="avatar" />
                                    <img v-else
                                        src="https://static.weryai.com/growth/image/9ead8a3f9b5dd9d44367d3aba6ac8a93.webp"
                                        :alt="item.user?.user_name
                                            ? `${item.user.user_name}'s avatar`
                                            : 'Default user avatar'
                                            " class="avatar" />

                                    <span class="username">{{
                                        item.user?.user_name ||
                                        t('inspiration.unknownUser')
                                        }}</span>
                                </div>

                                <div class="create" v-show="hoveredItemId === getInspirationId(item) && !isMobile && shouldShowGenerateSimilar(item)"
                                    @click="navigationChange(item)">
                                    {{ t('inspiration.generateSimilar') }}
                                </div>
                            </div>
                            <div class="item-stats">
                                <div v-if="!isMobile" class="more-wrapper" :class="{
                                    visible:
                                        hoveredItemId === getInspirationId(item),
                                }">
                                    <tool-menu-more-function :video-id="getInspirationId(item)"
                                        :handleDataItem="item" />
                                </div>

                                <!-- 点赞保持不变 -->
                                <span class="state-item" @click="changeLikeStateForItem($event, item)">
                                    {{ item.likes_num || 0 }}
                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 20 20"
                                        fill="none" v-show="!item.has_like">
                                        <path
                                            d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                            stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                            stroke-linejoin="round" fill="none" />
                                    </svg>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 20 20"
                                        fill="none" v-show="item.has_like">
                                        <path
                                            d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                            fill="#FF5353" />
                                    </svg>
                                </span>
                            </div>
                        </div>
                    </div><!-- 关闭 item-wrapper -->
                </div>
            </div>

            <!-- 空数据时显示 -->
            <div v-else class="empty-state">
                <img class="w-full max-w-[300px] h-auto aspect-[300/208] object-contain"
                    src="https://static.weryai.com/growth/image/48b2bf9f0fcba0cdd7c00550a00b978b.webp"
                    alt="empty-logo" />
                <div class="text-base text-text-adad mt-5">
                    {{ t('inspiration.noInspirationData') }}
                </div>
            </div>

            <!-- 底部加载更多状态 -->
            <div
                v-if="waterfall.items.length > 0"
                ref="loadMoreSentinelRef"
                class="load-more-sentinel"
                aria-hidden="true"
            ></div>
            <div v-if="loading && !initLoading && total_inspiration_data > 0" class="loading-indicator">
                <l-tailspin size="20" stroke="3" speed="0.9" color="white" class="mx-1" aria-hidden="true" />
            </div>

            <!-- 没有更多数据提示 -->
            <div v-if="!hasMore && waterfall.items.length > 0 && !loading" class="no-more-indicator">
                {{ t('inspiration.noMoreData') }}
            </div>
        </template>

        <!-- 详情弹窗（音乐/图片/视频共用） -->

        <MobileUgcDetailItemInfo v-if="showMask && isMobile" :showDataItemInfo="currentDetailItem" @closeMask="
            () => {
                showMask = false
            }
        " @changeLikeStateForItem="changeLikeStateForItem" />
        <tool-ugc-detail-item-info v-else-if="showMask" :showDataItemInfo="currentDetailItem" @closeMask="
            () => {
                showMask = false
            }
        " @changeLikeStateForItem="changeLikeStateForItem"></tool-ugc-detail-item-info>
    </div>
</template>

<style scoped lang="less">
.inspiration-root {
    width: 100%;
    height: 100%;
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    border-radius: 12px;

    &::-webkit-scrollbar {
        display: none;
    }
}

.loading-state {
    width: 100%;
    min-height: 400px;
    padding: 20px;
    box-sizing: border-box;
}

.loading-state--waterfall {
    padding: 0;
}

.inspiration-skeleton-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2px;

    @media (min-width: 768px) {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    @media (min-width: 1300px) {
        grid-template-columns: repeat(5, minmax(0, 1fr));
    }

    @media (min-width: 1600px) {
        grid-template-columns: repeat(6, minmax(0, 1fr));
    }
}

.inspiration-skeleton-card {
    position: relative;
    aspect-ratio: 271 / 366;
    overflow: hidden;
    background: rgba(28, 28, 30, 0.8);
}

.waterfall-skeleton-container {
    pointer-events: none;
}

.waterfall-skeleton-item {
    cursor: default;
    user-select: none;
}

.inspiration-skeleton-media,
.inspiration-skeleton-avatar,
.inspiration-skeleton-line,
.inspiration-skeleton-like,
.music-skeleton-badge,
.music-skeleton-vinyl,
.music-skeleton-avatar,
.music-skeleton-line,
.music-skeleton-like {
    background: rgba(28, 28, 30, 0.8);
}

.inspiration-skeleton-media {
    width: 100%;
    height: 100%;
}

.inspiration-skeleton-overlay {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    padding: 12px;
}

.inspiration-skeleton-user,
.music-skeleton-user-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.inspiration-skeleton-avatar,
.music-skeleton-avatar {
    width: 26px;
    height: 26px;
    border-radius: 999px;
    flex-shrink: 0;
}

.inspiration-skeleton-line {
    height: 12px;
    border-radius: 999px;

    &.inspiration-skeleton-line--user {
        width: 72px;
    }
}

.inspiration-skeleton-like,
.music-skeleton-like {
    width: 46px;
    height: 28px;
    border-radius: 8px;
    flex-shrink: 0;
}

.music-skeleton-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 2px;
}

.music-skeleton-card {
    min-height: 320px;
    padding: 18px;
    background: rgba(28, 28, 30, 0.8);
    display: flex;
    flex-direction: column;
}

.music-skeleton-badge {
    width: 68px;
    height: 24px;
    border-radius: 999px;
}

.music-skeleton-vinyl {
    width: 160px;
    height: 160px;
    border-radius: 999px;
    margin: 24px auto 28px;
}

.music-skeleton-line {
    height: 14px;
    border-radius: 999px;

    &.music-skeleton-line--title {
        width: 70%;
        margin-bottom: 18px;
    }

    &.music-skeleton-line--user {
        width: 76px;
        flex: 0 0 auto;
    }
}

.empty-state {
    width: 100%;
    height: 100%;
    min-height: 400px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.load-more-sentinel {
    width: 100%;
    height: 1px;
}

.waterfall-container {
    position: relative;
    width: 100%;
    /* 撑满父容器，通过动态列宽填满 */
    min-height: 400px;
    transition: height 0.3s ease;
    padding: 20px;
    /* 添加内边距 */
    box-sizing: border-box;
}

.waterfall-item {
    border-radius: 8px;
    overflow: hidden;
    background: rgba(28, 28, 30, 0.8);
    cursor: pointer;

    .media-content {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
}

/* 内部包裹层，提供相对定位参照 */
.item-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: inherit;

    .media-content {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }

    /* 内容信息层（悬浮显示） */
    .item-info {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        gap: 8px;
        min-width: 0;
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 12px;
        background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
        color: white;
        opacity: 1;
        transition: opacity 0.2s ease;

        /* 随卡片宽度分配空间：避免窄列时「做同款」与右侧统计被裁成半截 */
        .left-info {
            flex: 1 1 auto;
            min-width: 0;
            max-width: calc(100% - 72px);
            overflow: hidden;
            text-overflow: ellipsis;

            .username {
                max-width: 150px;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }

        .create {
            font-size: 0.75rem;
            background: #99f768;
            padding: 8px 14px;
            border-radius: 8px;
            color: #0d0e12;
            width: fit-content;
            margin-top: 8px;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
        }
    }
}

.preview-overlay-visible {
    opacity: 1;
}

.preview-loading-spinner {
    animation: preview-spin 1s linear infinite;
}

@keyframes preview-spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

@media (min-width: 768px) {
    .waterfall-container {
        padding: 0;
        overflow: hidden;
        border-top-left-radius: 12px;
        border-top-right-radius: 12px;
    }

    .waterfall-item,
    .item-wrapper {
        border-radius: 0;
    }
}

.item-wrapper--tool-page-pc {
    .item-info {
        opacity: 0;
        pointer-events: none;
    }

    &:hover {
        .item-info {
            opacity: 1;
            pointer-events: auto;
        }
    }
}

.user-info {
    display: flex;
    align-items: center;
    column-gap: 5px;
    margin-bottom: 3px;

    img.avatar {
        width: 25px;
        height: 25px;
        object-fit: cover;
        border-radius: 50%;
    }

    .username {
        font-size: 14px;
        font-weight: 200;
        white-space: nowrap;
    }
}

.item-stats {
    display: flex;
    flex-shrink: 0;
    gap: 5px;

    .more-wrapper {
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.15s ease;

        &.visible {
            opacity: 1;
            pointer-events: auto;
        }
    }

    .state-item {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;
        padding: 3px 10px;
        user-select: none;
        background-color: rgba(0, 0, 0, 0.6);
        height: 26px;
        border-radius: 6px;

        &:hover {
            background-color: rgba(13, 14, 18, 0.6);
        }

        i {
            width: 16px;
            height: 16px;
            /* 这里可以使用实际的图标 */
        }
    }
}

/* 响应式调整 */
@media (max-width: 768px) {
    .waterfall-container {
        padding: 8px;
    }
}

/* 加载指示器样式 */
.loading-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 30px;
}

.no-more-indicator {
    text-align: center;
    padding: 10px;
    color: #8b8d98;
    font-size: 14px;
}

// ==================== 音乐类型卡片样式 ====================

.music-grid-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, 236px);
    gap: 16px;
    justify-content: start;
}

.music-card {
    width: 236px;
    height: 320px;
    border-radius: 18px;
    border: 1px solid rgba(255, 255, 255, 0.05);
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
    box-sizing: border-box;
}

// 背景层：图片 + 高斯模糊
.music-card-bg {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    filter: blur(20px);
    transform: scale(1.1);
    z-index: 0;

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.3);
    }
}

// 左上角类型标签
.music-type-tag {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 4px;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 5px;
    height: 22px;
    padding: 0 8px;

    span {
        font-size: 9px;
        color: #fff;
        line-height: 1;
    }
}

// 黑胶唱片
.vinyl-record {
    width: 210px;
    height: 210px;
    position: relative;
    z-index: 1;
    margin-top: 16px;
    flex-shrink: 0;

    &.spinning {
        .vinyl-outer {
            animation: spin 6s linear infinite;
        }

        .vinyl-inner {
            animation: spin-center 6s linear infinite;
        }
    }
}

.vinyl-outer {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background-color: rgba(8, 8, 8, 0.5);
}

.vinyl-inner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 187px;
    height: 187px;
    border-radius: 50%;
    overflow: hidden;
}

.vinyl-cover {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.vinyl-center-dot {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 1);
    border: 2px solid rgba(51, 51, 51, 1);
    box-sizing: border-box;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

@keyframes spin-center {
    from {
        transform: translate(-50%, -50%) rotate(0deg);
    }

    to {
        transform: translate(-50%, -50%) rotate(360deg);
    }
}

// 底部区域：常态信息 + 悬浮操作
// 常态信息区
.info-section {
    width: 100%;
    margin-top: auto;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    transition: opacity 0.25s ease;
}

// 歌曲名称行
.song-title-row {
    display: flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
}

.song-title {
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: calc(100% - 50px); // 预留类型标签空间
}

// 歌曲类型标签
.song-type-tag {
    height: 18px;
    padding: 0 8px;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 20px;
    font-size: 9px;
    color: #fff;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    white-space: nowrap;
}

// 用户信息行
.user-info-row {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.user-left {
    display: flex;
    align-items: center;
    gap: 6px;
    overflow: hidden;
}

.user-avatar {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 1);
    object-fit: cover;
    flex-shrink: 0;
}

.user-name {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100px;
}

.like-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.7);

    span {
        font-size: 12px;
    }

    &:hover {
        opacity: 0.8;
    }
}

// 悬浮：操作按钮覆盖层
// Hover 覆盖层：带高斯模糊背景
.hover-overlay {
    position: absolute;
    inset: 0;
    z-index: 10;
    display: flex;
    flex-direction: column;
    padding: 16px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.25s ease;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
}

// 歌词滚动区域
.lyrics-area {
    flex: 1;
    overflow-y: auto;
    margin-bottom: 12px;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        display: none;
    }
}

.lyrics-text {
    font-size: 12px;
    line-height: 30px;
    color: #fff;
    white-space: pre-wrap;
    word-break: break-word;
    text-align: center;
}

// 操作按钮行
.action-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
}

.music-card:hover {
    .info-section {
        opacity: 0;
    }

    .hover-overlay {
        opacity: 1;
        pointer-events: auto;
    }
}

.play-btn {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(167, 171, 178, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(167, 171, 178, 0.5);
    }
}

.hover-like-btn {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(167, 171, 178, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(167, 171, 178, 0.5);
    }
}

.make-similar-btn {
    flex: 1;
    height: 30px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(167, 171, 178, 0.3);
    color: #fff;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;

    &:hover {
        background: rgba(167, 171, 178, 0.5);
    }
}

/* 瀑布流 hover 工具盒子样式 - opacity 控制 */
.waterfall-hover-tools {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
}

/* Hover 时显示工具盒子 */
.item-wrapper:hover .waterfall-hover-tools {
    opacity: 1;
    pointer-events: auto;
}

/* 瀑布流 hover 图标样式 */
.i-upload-plus-icon {
    mask: url('~/assets/images/workbench/upload-plus.svg') no-repeat center;
    -webkit-mask: url('~/assets/images/workbench/upload-plus.svg') no-repeat center;
    mask-size: contain;
    -webkit-mask-size: contain;
    background-color: currentColor;
}

.i-image-edit-icon {
    mask: url('~/assets/images/menu/image-edit.svg') no-repeat center;
    -webkit-mask: url('~/assets/images/menu/image-edit.svg') no-repeat center;
    mask-size: contain;
    -webkit-mask-size: contain;
    background-color: currentColor;
}

.i-video-edit-icon {
    mask: url('~/assets/images/menu/video-edit.svg') no-repeat center;
    -webkit-mask: url('~/assets/images/menu/video-edit.svg') no-repeat center;
    mask-size: contain;
    -webkit-mask-size: contain;
    background-color: currentColor;
}
</style>

<!-- 非 scoped 样式，用于覆盖全局样式 -->
<style lang="less">
/* 瀑布流 hover 工具盒子 - 强制黑色半透明背景 */
.waterfall-hover-tools {
    background: rgba(0, 0, 0, 0.70) !important;
    backdrop-filter: blur(6px) !important;
    -webkit-backdrop-filter: blur(6px) !important;
}
</style>
